import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react';
import ViewWrapper from './ViewWrapper';
import { useStore } from '../../../../store/store';
import { IPreviewShareLink } from '../../../../store/previewStore';
import ShareLinkDialog from '../../../../components/previewCourse/ShareLinkDialog';
import useConfirm from '../../../../hooks/useConfirm';
import SnackbarUtils from '../../../../components/snackbarUtils/SnackbarUtilsConfigurator';
import { formatPreviewDate } from '../../../../utils/previewDates';
import { findPreviewPageLabel } from '../../../../utils/previewPages';

interface PreviewLinksViewProps {
  courseID: number;
  courseTitle: string;
}

function PreviewLinksView({ courseID, courseTitle }: PreviewLinksViewProps) {
  const { previewStore, courseStore } = useStore();
  const theme = useTheme();
  const [links, setLinks] = useState<IPreviewShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<IPreviewShareLink | null>(null);
  const [pendingActionPublicId, setPendingActionPublicId] = useState('');
  const [RevokeDialog, confirmRevoke] = useConfirm('Revoke preview link?', 'This will immediately block access to this link.', true);
  const [DeleteDialog, confirmDelete] = useConfirm('Delete preview link?', 'This permanently removes this link and cannot be undone.', true);

  const copyLink = useCallback(async (link: IPreviewShareLink) => {
    try {
      await navigator.clipboard.writeText(link.shareUrl);
      SnackbarUtils.success('Preview link copied');
    } catch {
      SnackbarUtils.error('Unable to copy preview link.');
    }
  }, []);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allLinks = await previewStore.listShareLinks(courseID, true);
      setLinks(allLinks);
    } catch {
      setError('Unable to load preview links.');
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [previewStore, courseID]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  const openEditDialog = (link: IPreviewShareLink) => {
    setEditingLink(link);
  };

  const closeEditDialog = () => {
    setEditingLink(null);
  };

  const revokeLink = async (link: IPreviewShareLink) => {
    const confirmed = await confirmRevoke();
    if (!confirmed) {
      return;
    }

    setPendingActionPublicId(link.publicId);
    try {
      await previewStore.revokeShareLink(link.publicId);
      SnackbarUtils.success('Preview link revoked');
      await loadLinks();
    } catch {
      SnackbarUtils.error('Unable to revoke preview link.');
    } finally {
      setPendingActionPublicId('');
    }
  };

  const deleteLink = async (link: IPreviewShareLink) => {
    const confirmed = await confirmDelete();
    if (!confirmed) {
      return;
    }

    setPendingActionPublicId(link.publicId);
    try {
      await previewStore.deleteShareLink(link.publicId);
      SnackbarUtils.success('Preview link deleted');
      await loadLinks();
    } catch {
      SnackbarUtils.error('Unable to delete preview link.');
    } finally {
      setPendingActionPublicId('');
    }
  };

  const hasLinks = useMemo(() => links.length > 0, [links]);

  return (
    <>
      <ViewWrapper title='Preview Links' courseTitle={courseTitle}>
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Manage all preview links for this course.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant='outlined' startIcon={<RefreshIcon />} onClick={loadLinks} disabled={loading}>
                Refresh
              </Button>
              <Button variant='contained' startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
                Create Link
              </Button>
            </Box>
          </Box>

          {error && <Alert severity='error'>{error}</Alert>}

          <Box sx={{ flex: '1 1 auto', background: theme.palette.bgTwo.main, borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: '1 1 auto' }}>
                <CircularProgress size={26} />
              </Box>
            ) : !hasLinks ? (
              <Box sx={{ display: 'grid', placeItems: 'center', flex: '1 1 auto', color: theme.palette.text.secondary }}>
                <Typography variant='body2'>No preview links created yet.</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ flex: '1 1 auto' }}>
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell>Start Page</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell>Views</TableCell>
                      <TableCell align='right'>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {links.map((link) => {
                      const isPending = pendingActionPublicId === link.publicId;
                      return (
                        <TableRow key={link.publicId} hover>
                          <TableCell>{link.previewName?.trim() || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 320, minWidth: 250 }}>
                            <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                              {link.shareUrl}
                            </Typography>
                          </TableCell>
                          <TableCell>{findPreviewPageLabel(courseStore.courseViewModel?.pages, link.coursePageId)}</TableCell>
                          <TableCell>
                            {link.isRevoked ? 'Revoked' : link.isExpired ? 'Expired' : typeof link.maxViews === 'number' && link.viewCount >= link.maxViews ? 'Max Views Reached' : 'Active'}
                          </TableCell>
                          <TableCell>{formatPreviewDate(link.createdAtUtc)}</TableCell>
                          <TableCell>{formatPreviewDate(link.expiresAtUtc)}</TableCell>
                          <TableCell>
                            {link.viewCount}
                            {typeof link.maxViews === 'number' ? ` / ${link.maxViews}` : ''}
                          </TableCell>
                          <TableCell align='right' sx={{ minWidth: 150 }}>
                            <Tooltip title='Copy link'>
                              <span>
                                <IconButton size='small' onClick={() => void copyLink(link)} disabled={isPending}>
                                  <ContentCopyIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Edit link'>
                              <span>
                                <IconButton size='small' onClick={() => openEditDialog(link)} disabled={isPending}>
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Revoke link'>
                              <span>
                                <IconButton size='small' onClick={() => revokeLink(link)} disabled={isPending || link.isRevoked}>
                                  <BlockIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Delete link'>
                              <span>
                                <IconButton size='small' onClick={() => deleteLink(link)} disabled={isPending}>
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </ViewWrapper>

      {createDialogOpen ? <ShareLinkDialog open onClose={closeCreateDialog} courseId={courseID} coursePageId={undefined} onSaved={() => void loadLinks()} /> : null}

      {editingLink ? <ShareLinkDialog open onClose={closeEditDialog} courseId={courseID} mode='edit' link={editingLink} onSaved={() => void loadLinks()} /> : null}

      <RevokeDialog />
      <DeleteDialog />
    </>
  );
}

export default observer(PreviewLinksView);
